import { useEffect, useMemo, useState, type ReactNode } from "react";

interface ChatAvatarProps {
  title: string;
  avatarUrl?: string | null | undefined;
  className: string;
  children?: ReactNode;
  // «Избранное»: вместо своего фото/инициалов — закладка на голубом фоне,
  // как в оригинальном Telegram.
  savedMessages?: boolean;
}

const avatarFallback = (title: string): string => {
  const trimmed = title.trim();
  if (trimmed.length === 0) {
    return "TG";
  }

  return trimmed.slice(0, 2).toUpperCase();
};

const MAX_AVATAR_RETRIES = 3;

const buildAvatarRequestUrl = (avatarUrl: string, retryAttempt: number): string => {
  if (retryAttempt === 0 || typeof window === "undefined") {
    return avatarUrl;
  }

  try {
    const url = new URL(avatarUrl, window.location.origin);
    url.searchParams.set("avatar_retry", String(retryAttempt));
    return url.toString();
  } catch {
    const separator = avatarUrl.includes("?") ? "&" : "?";
    return `${avatarUrl}${separator}avatar_retry=${retryAttempt}`;
  }
};

export const ChatAvatar = ({
  title,
  avatarUrl,
  className,
  children,
  savedMessages
}: ChatAvatarProps) => {
  const [hasError, setHasError] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);

  useEffect(() => {
    setHasError(false);
    setRetryAttempt(0);
  }, [avatarUrl]);

  useEffect(() => {
    if (!avatarUrl || !hasError || retryAttempt >= MAX_AVATAR_RETRIES) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setHasError(false);
      setRetryAttempt((current) => current + 1);
    }, 400 * retryAttempt + 600);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [avatarUrl, hasError, retryAttempt]);

  const requestUrl = useMemo(() => {
    if (!avatarUrl || avatarUrl.startsWith("data:")) {
      return null;
    }
    return buildAvatarRequestUrl(avatarUrl, retryAttempt);
  }, [avatarUrl, retryAttempt]);

  if (savedMessages) {
    return (
      <div className={`${className} chat-avatar--saved`}>
        <i className="ic ic-saved chat-avatar__saved-icon" aria-hidden="true" />
        {children}
      </div>
    );
  }

  return (
    <div className={className}>
      <span className="chat-avatar__fallback">{avatarFallback(title)}</span>
      {requestUrl && !hasError ? (
        <img
          src={requestUrl}
          alt={title}
          loading="lazy"
          decoding="async"
          className="chat-avatar__image"
          onError={() => setHasError(true)}
        />
      ) : null}
      {children}
    </div>
  );
};
